import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { apiAdmin, setSuperAdminToken } from '@/api/apiClient';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';

export default function AdminConfigAsaas() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [erro, setErro] = useState('');
  const [form, setForm] = useState({
    apiKey: '',
    apiUrl: 'https://sandbox.asaas.com/api/v3',
    webhookSecret: '',
  });

  const carregar = async () => {
    setLoading(true);
    setErro('');
    try {
      const data = await apiAdmin.get('/api/admin/config/asaas');
      setConfig(data);
      setForm((prev) => ({
        ...prev,
        apiUrl: data.apiUrl || prev.apiUrl,
        apiKey: data.apiKeyConfigured ? '********' : '',
        webhookSecret: data.webhookSecretConfigured ? '********' : (data.webhookSecret || ''),
      }));
    } catch (err) {
      if (err?.status === 401) {
        setSuperAdminToken(null);
        window.location.href = '/admin';
        return;
      }
      setErro(err?.message || 'Erro ao carregar configuração');
      toast.error('Erro ao carregar configuração');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregar();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setErro('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setErro('');
    try {
      const payload = {
        apiUrl: form.apiUrl?.trim() || undefined,
        apiKey: form.apiKey === '********' ? undefined : (form.apiKey?.trim() || ''),
        webhookSecret: form.webhookSecret === '********' ? undefined : (form.webhookSecret?.trim() || ''),
      };
      const data = await apiAdmin.put('/api/admin/config/asaas', payload);
      setConfig(data);
      setForm((prev) => ({
        ...prev,
        apiUrl: data.apiUrl || prev.apiUrl,
        apiKey: data.apiKeyConfigured ? '********' : '',
        webhookSecret: data.webhookSecretConfigured ? '********' : (data.webhookSecret || ''),
      }));
      toast.success('Configuração salva com sucesso.');
    } catch (err) {
      setErro(err?.message || 'Erro ao salvar');
      toast.error('Erro ao salvar configuração');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 max-w-2xl mx-auto space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-2xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Configuração ASAAS</h1>
        <p className="text-slate-600">API e Webhook para integração com o gateway de pagamentos</p>
      </div>

      {erro && (
        <Alert variant="destructive">
          <AlertDescription>{erro}</AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Credenciais</CardTitle>
          <CardDescription>Configure a API Key e URL do ASAAS para processar pagamentos</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="apiKey">API Key</Label>
              <Input
                id="apiKey"
                type="password"
                name="apiKey"
                value={form.apiKey}
                onChange={handleChange}
                placeholder={config?.apiKeyConfigured ? 'Deixe em branco para manter a atual' : 'Sua chave de API ASAAS'}
                autoComplete="off"
              />
              {config?.apiKeyConfigured && (
                <p className="text-xs text-slate-500">Chave já configurada. Informe uma nova apenas para alterar.</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="apiUrl">URL da API</Label>
              <Input
                id="apiUrl"
                type="url"
                name="apiUrl"
                value={form.apiUrl}
                onChange={handleChange}
                placeholder="https://sandbox.asaas.com/api/v3"
              />
              <p className="text-xs text-slate-500">
                Sandbox: https://sandbox.asaas.com/api/v3 — Produção: https://www.asaas.com/api/v3
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="webhookSecret">Webhook Secret (opcional)</Label>
              <Input
                id="webhookSecret"
                type="password"
                name="webhookSecret"
                value={form.webhookSecret}
                onChange={handleChange}
                placeholder={config?.webhookSecretConfigured ? 'Deixe em branco para manter' : 'Token de validação do webhook'}
                autoComplete="off"
              />
            </div>

            <div className="space-y-2">
              <Label>URL do Webhook (somente leitura)</Label>
              <Input
                readOnly
                value={config?.webhookUrl ?? ''}
                className="bg-slate-50 font-mono text-xs"
              />
              <p className="text-xs text-slate-500">Configure esta URL no painel ASAAS em Integrações → Webhook.</p>
            </div>

            <Button type="submit" disabled={saving}>
              {saving ? 'Salvando...' : 'Salvar'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
